import {
  Box,
  type BoxProps,
  Card,
  type CardProps,
  Group,
  type GroupProps,
  Text,
  type TextProps,
} from '@mantine/core';
import cx from 'clsx';
import type { MouseEventHandler, PropsWithChildren, ReactNode } from 'react';
import classes from './TitleCard.module.css';

export type TitleCardProps = CardProps & {
  title: ReactNode;
  leftSection?: ReactNode;
  rightSection?: ReactNode;
  titleProps?: TextProps;
  headSectionProps?: GroupProps;
  bodySectionProps?: BoxProps;
  onClick?: MouseEventHandler<HTMLDivElement>;
};

/**
 * Renders a title card component with a title, optional left and right sections, and a content body.
 * The card supports customizable properties and provides event handling for click actions.
 *
 * @param title The main title displayed at the top of the card.
 * @param leftSection An optional section rendered to the left of the title.
 * @param rightSection An optional section rendered to the right of the title.
 * @param children The content rendered in the body of the card.
 * @param titleProps Additional properties applied to customize the title text.
 * @param headSectionProps Additional properties applied to the header section of the card.
 * @param bodySectionProps Additional properties applied to the body section of the card.
 * @param onClick A callback function invoked when the card is clicked. If provided, the card gains a clickable role.
 * @param className Additional class names applied to the card.
 * @param cardProps Any other props forwarded to the underlying Card component.
 * @return A title card component with a configurable layout for header and body sections.
 */
export function TitleCard({
  title,
  leftSection,
  rightSection,
  children,
  titleProps,
  headSectionProps,
  bodySectionProps,
  onClick,
  className,
  ...cardProps
}: PropsWithChildren<TitleCardProps>) {
  return (
    <Card
      withBorder
      {...cardProps}
      className={cx(classes.root, onClick && classes.clickable, className)}
      role={onClick ? 'button' : undefined}
      onClick={onClick}
      tabIndex={onClick ? 0 : undefined}
    >
      <Group
        gap="xs"
        wrap="nowrap"
        {...headSectionProps}
        className={cx(classes.head, headSectionProps?.className)}
      >
        {leftSection}
        <Text
          fw={500}
          {...titleProps}
          className={cx(classes.title, titleProps?.className)}
        >
          {title}
        </Text>
        {rightSection}
      </Group>
      <Box
        {...bodySectionProps}
        className={cx(classes.body, bodySectionProps?.className)}
      >
        {children}
      </Box>
    </Card>
  );
}
